import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * 강제 비밀번호 변경 화면.
 *
 * xadmin_account(관리자 계정 등록 화면, server/src/modules/admins 참고)로
 * 만든 계정은 관리자가 정해준 초기 비밀번호로 로그인한다 - 그 비밀번호를
 * 본인이 직접 바꾸기 전까지는 이 화면 밖으로 못 나간다. App.tsx 가
 * user.mustChangePassword 를 보고 로그인 여부와 무관하게 이 화면을 강제로
 * 띄운다(다른 라우트는 전부 가린다). 서버도 같은 걸 한 번 더 막는다
 * (middleware/requirePasswordChanged.ts) - 여기 화면만 우회해도 API 는 여전히
 * 막혀 있다.
 */

const MIN_PASSWORD_LENGTH = 12;

export function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('현재 비밀번호와 다른 값으로 바꿔주세요.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : '비밀번호 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login">
      <form className="login__card" onSubmit={onSubmit}>
        <h1 className="login__title">비밀번호 변경</h1>
        <p className="login__sub">
          {user?.userid} 님, 처음 로그인하셨네요. 계속하려면 비밀번호부터 바꿔주세요.
        </p>

        <label className="field">
          <span className="field__label">현재 비밀번호</span>
          <input
            className="input"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span className="field__label">새 비밀번호 ({MIN_PASSWORD_LENGTH}자 이상)</span>
          <input
            className="input"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">새 비밀번호 확인</span>
          <input
            className="input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </label>

        {error && (
          <div className="state state--error" role="alert">
            <span>{error}</span>
          </div>
        )}

        <button className="btn btn--primary btn--block" type="submit" disabled={submitting}>
          {submitting ? '변경 중…' : '비밀번호 변경'}
        </button>
        <button className="btn btn--ghost btn--block" type="button" onClick={() => void logout()}>
          로그아웃
        </button>
      </form>
    </div>
  );
}
